import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode as DemandSearchTrendsKeywords } from "@similarweb/pro-api-client";
import { Table, useTableSelectionController } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { useApiQuery } from "app/modules/api-client";
import { createKeywordGroupEntity } from "app/modules/demand-analysis/keyword-lists";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { useTableSelectionButtons } from "pro-features/components/TableSelection/src/hooks/useTableSelectionButtons";
import { CreateOrUpdateKeywordListPopoverWrapper } from "pro-features/components/TableSelection/src/single-responsibility-buttons/keyword-list/CreateOrUpdateKeywordListPopoverWrapper";
import { SearchTrackerFromKeywordsTablePopover } from "pro-features/components/TableSelection/src/single-responsibility-buttons/search-tracker/SearchTrackerPopover";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import { getSearchTrendsExcelURL } from "../../utils/getExcelURL";

import { KeywordsBreakdownQuickFilters, type QuickFilterId } from "./KeywordsBreakdownQuickFilters";
import { getTableColumns } from "./KeywordsBreakdownTableColumns";
import { useKeywordsBreakdownQuickFilterIntent } from "./useKeywordsBreakdownQuickFilterIntent";
import {
    processKeywordsBreakdownData,
    type KeywordsBreakdownSortState,
    type KeywordsBreakdownTableRow,
} from "./keywordsBreakdownTableDataSelector";

const DEFAULT_SORT: KeywordsBreakdownSortState = {
    columnKey: "VolumeShare",
    order: "descend",
};

const KEYWORDS_BREAKDOWN_PAGE_SIZE = 50;
const QUICK_FILTER_VOLUME_SHARE_THRESHOLD = 0.0017;

const KeywordsBreakdownTableWrapper = styled.div`
    padding-bottom: 32px;

    .ant-table-row:hover .link-buttons-container {
        display: inline-block;
        opacity: 1;
        transform: scale(1);
    }
`;

interface IKeywordsBreakdownTableProps {
    isPeriodOverPeriod?: boolean;
    rowLimit?: number;
}

export const KeywordsBreakdownTable = ({
    isPeriodOverPeriod = false,
    rowLimit = KEYWORDS_BREAKDOWN_PAGE_SIZE,
}: IKeywordsBreakdownTableProps) => {
    const { params } = useCurrentStateAndParams();
    const [sortConfig, setSortConfig] = useState<KeywordsBreakdownSortState>(DEFAULT_SORT);
    const [filter, setFilter] = useState("");
    const [quickFilter, setQuickFilter] = useState<QuickFilterId | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);

    const requestedQuickFilter = useKeywordsBreakdownQuickFilterIntent((state) => state.requestedQuickFilter);
    const consumeRequestedQuickFilter = useKeywordsBreakdownQuickFilterIntent(
        (state) => state.consumeRequestedQuickFilter,
    );

    const queryParams = useKeywordListsNodeAPIQueryParams();

    const select = useCallback((result: DemandSearchTrendsKeywords) => processKeywordsBreakdownData(result), []);
    const { data: allRows, isLoading } = useApiQuery(
        {
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
            options: { select },
        },
        queryParams,
    );

    const quickFilterCounts = useMemo<Record<QuickFilterId, number>>(() => {
        const counts: Record<QuickFilterId, number> = { emerging: 0, declining: 0, new: 0 };
        for (const row of allRows ?? []) {
            if (row.TotalVolumeChange == null) {
                counts.new += 1;
            } else if (row.VolumeShare > QUICK_FILTER_VOLUME_SHARE_THRESHOLD) {
                if (row.TotalVolumeChange > 0) {
                    counts.emerging += 1;
                } else if (row.TotalVolumeChange < 0) {
                    counts.declining += 1;
                }
            }
        }
        return counts;
    }, [allRows]);

    const filteredSortedRows = useMemo<KeywordsBreakdownTableRow[]>(() => {
        if (!allRows) {
            return [];
        }

        let rows = allRows;

        if (quickFilter === "new") {
            rows = rows.filter((row) => row.TotalVolumeChange == null);
        } else if (quickFilter === "emerging") {
            rows = rows.filter(
                (row) =>
                    row.TotalVolumeChange != null &&
                    row.TotalVolumeChange > 0 &&
                    row.VolumeShare > QUICK_FILTER_VOLUME_SHARE_THRESHOLD,
            );
        } else if (quickFilter === "declining") {
            rows = rows.filter(
                (row) =>
                    row.TotalVolumeChange != null &&
                    row.TotalVolumeChange < 0 &&
                    row.VolumeShare > QUICK_FILTER_VOLUME_SHARE_THRESHOLD,
            );
        }

        const query = filter.toLowerCase().trim();
        if (query) {
            rows = rows.filter((row) => row.SearchTerm.toLowerCase().includes(query));
        }

        if (sortConfig) {
            const isAscending = sortConfig.order === "ascend";
            const { columnKey } = sortConfig;
            const sorted = [...rows].sort((left, right) => {
                if (columnKey === "SearchTerm") {
                    const cmp = (left.SearchTerm ?? "").localeCompare(right.SearchTerm ?? "", undefined, {
                        sensitivity: "base",
                    });
                    return isAscending ? cmp : -cmp;
                }
                const leftValue = (left[columnKey as keyof KeywordsBreakdownTableRow] as number | undefined) ?? 0;
                const rightValue = (right[columnKey as keyof KeywordsBreakdownTableRow] as number | undefined) ?? 0;
                const cmp = leftValue - rightValue;
                return isAscending ? cmp : -cmp;
            });
            rows = sorted;
        }

        return rows;
    }, [allRows, filter, quickFilter, sortConfig]);

    const pageRows = useMemo<KeywordsBreakdownTableRow[]>(() => {
        const pageStart = (currentPage - 1) * rowLimit;
        return filteredSortedRows.slice(pageStart, pageStart + rowLimit);
    }, [filteredSortedRows, currentPage, rowLimit]);

    const { duration, comparedDuration, country } = params;

    const handleViewKeywordGroupClick = useCallback(
        (groupId: string) => {
            const keywordGroupEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: groupId },
                groupId,
            );
            proNavigator.go(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                {
                    id: [keywordGroupEntity.keywordListId],
                    country,
                    duration,
                    ignoreFilterConsistency: true,
                },
                { inherit: false },
            );
        },
        [country, duration],
    );

    const buildKeywordGroupToastHref = useCallback(
        (groupId: string) => {
            const keywordGroupEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: groupId },
                groupId,
            );
            return proNavigator.href(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                {
                    id: [keywordGroupEntity.keywordListId],
                    country,
                    duration,
                    ignoreFilterConsistency: true,
                },
                { inherit: false },
            );
        },
        [country, duration],
    );
    const { from, to, compareFrom, compareTo, isWindow } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    const periodOverPeriodType = DurationService.getPeriodOverPeriodCompareType();
    const tableColumns = useMemo(
        () =>
            getTableColumns({
                periodOverPeriodType,
                sortedColumn: sortConfig,
                duration: isPeriodOverPeriod ? { from, to, compareFrom, compareTo } : { from, to },
                isWindow,
                totalCount: filteredSortedRows.length,
            }),
        [
            periodOverPeriodType,
            sortConfig,
            isPeriodOverPeriod,
            from,
            to,
            compareFrom,
            compareTo,
            isWindow,
            filteredSortedRows.length,
        ],
    );

    const isExcelAllowed = Boolean(swSettings.current.resources.IsExcelAllowed);
    const excelURL = getSearchTrendsExcelURL(queryParams);

    const selectionController = useTableSelectionController<KeywordsBreakdownTableRow>({
        allRows: filteredSortedRows,
        pageRows,
        rowKey: "SearchTerm",
        page: currentPage,
        pageSize: rowLimit,
    });

    const selectedRowKeys = selectionController.state.selectedRowKeys;
    const selectedRowsKeywords = useMemo(() => selectedRowKeys.map((key) => String(key)), [selectedRowKeys]);

    const setSelectedRowsAdapter = useCallback<Dispatch<SetStateAction<Record<string, unknown>[]>>>(
        (nextOrUpdater) => {
            const next = typeof nextOrUpdater === "function" ? nextOrUpdater([]) : nextOrUpdater;
            if (next.length === 0) {
                selectionController.actions.clearAllSelection();
            }
        },
        [selectionController.actions],
    );

    const {
        actionButtons,
        clearAllSelectedRows,
        keywordListButtonRef,
        searchTrackerButtonRef,
        isKeywordListPopoverOpen,
        setIsKeywordListPopoverOpen,
        isCreateNewListModalOpen,
        setIsCreateNewListModalOpen,
        isSearchTrackerPopoverOpen,
        setIsSearchTrackerPopoverOpen,
    } = useTableSelectionButtons(setSelectedRowsAdapter);

    const handleSearchChange = useCallback((value: string) => {
        setFilter(value);
        setCurrentPage(1);
    }, []);

    const handleQuickFilterChange = useCallback(
        (next: QuickFilterId | undefined) => {
            setQuickFilter(next);
            setCurrentPage(1);
            selectionController.actions.clearAllSelection();
        },
        [selectionController.actions],
    );

    // Apply a quick filter requested from elsewhere (e.g. the New Keywords card's "See full list" link).
    useEffect(() => {
        if (requestedQuickFilter) {
            handleQuickFilterChange(requestedQuickFilter);
            consumeRequestedQuickFilter();
        }
    }, [requestedQuickFilter, handleQuickFilterChange, consumeRequestedQuickFilter]);

    const handleSort = useCallback<
        NonNullable<React.ComponentProps<typeof Table<KeywordsBreakdownTableRow>>["onSort"]>
    >((sorter) => {
        const nextSort: KeywordsBreakdownSortState =
            sorter?.columnKey && sorter?.order
                ? { columnKey: String(sorter.columnKey), order: sorter.order }
                : DEFAULT_SORT;
        setSortConfig(nextSort);
        setCurrentPage(1);
    }, []);

    const handlePaginationChange = useCallback(
        (_prev: number, _pageSize: number, nextPage: number) => {
            const next = nextPage ?? 1;
            selectionController.actions.handlePageChange(next);
            setCurrentPage(next);
        },
        [selectionController.actions],
    );

    const trackExcelDownload = useCallback(() => {
        allTrackers.dispatchTrackEvent("download", "submit-ok", "Table", {
            event_sub_name: "Excel",
            event_sub_sub_name: "Single cluster",
        });
    }, []);

    const selectionCountText = useMemo(() => {
        const key = selectedRowKeys.length === 1 ? "table.selection.keywords" : "table.selection.keywords.multiple";
        return translate(key, { count: selectedRowKeys.length.toString() });
    }, [selectedRowKeys.length]);

    const noDataConfig = useMemo(
        () => ({
            title: translate("demand.analysis.search.trends.keywords.breakdown.table.nodata.title"),
            subtitle: translate("demand.analysis.search.trends.keywords.breakdown.table.subtitle"),
        }),
        [],
    );

    return (
        <KeywordsBreakdownTableWrapper data-automation="keywords-breakdown-table">
            <KeywordsBreakdownQuickFilters
                selected={quickFilter}
                onChange={handleQuickFilterChange}
                counts={quickFilterCounts}
                isLoading={isLoading}
            />
            <Table<KeywordsBreakdownTableRow>
                rowKey="SearchTerm"
                data={pageRows}
                columnConfig={tableColumns}
                isLoading={isLoading}
                loaderRowCount={Math.min(rowLimit, 10)}
                sticky={false}
                onSort={handleSort}
                pagination={{
                    pageSize: rowLimit,
                    total: filteredSortedRows.length,
                    current: currentPage,
                    onChange: handlePaginationChange,
                }}
                selection={{ ...selectionController.tableSelection, columnWidth: 40 }}
                noData={noDataConfig}
            >
                <Table.TableTop>
                    <Table.TableTop.Filters
                        filters={[
                            {
                                filter: "search",
                                onSearchChange: handleSearchChange,
                                initialValue: filter,
                                placeholder: translate("forms.search.placeholder"),
                            },
                        ]}
                    />
                    <Table.TableTop.Settings
                        excelExportConfig={{
                            excelDownloadUrl: isExcelAllowed ? excelURL : "",
                            onExcelDownloaded: trackExcelDownload,
                            totalCount: filteredSortedRows.length,
                        }}
                    />
                    <Table.TableTop.Selection
                        hideCount
                        selectText={selectionCountText}
                        selectAllDropdown={selectionController.selectAllDropdown}
                        actionButtons={actionButtons}
                    />
                </Table.TableTop>
            </Table>
            <CreateOrUpdateKeywordListPopoverWrapper
                selectedRows={selectedRowsKeywords}
                keywordGroupState={KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE}
                onViewGroupClick={handleViewKeywordGroupClick}
                getToastHref={buildKeywordGroupToastHref}
                isSearchTrends
                clearAllSelectedRows={clearAllSelectedRows}
                isKeywordListPopoverOpen={isKeywordListPopoverOpen}
                setIsKeywordListPopoverOpen={setIsKeywordListPopoverOpen}
                isCreateNewListModalOpen={isCreateNewListModalOpen}
                setIsCreateNewListModalOpen={setIsCreateNewListModalOpen}
                keywordListButtonRef={keywordListButtonRef}
                isKeywordRemovalModalOpen={false}
                setIsKeywordRemovalModalOpen={() => undefined}
            />
            <SearchTrackerFromKeywordsTablePopover
                selectedRows={selectedRowsKeywords}
                searchTrackerButtonRef={searchTrackerButtonRef}
                isSearchTrackerPopoverOpen={isSearchTrackerPopoverOpen}
                setIsSearchTrackerPopoverOpen={setIsSearchTrackerPopoverOpen}
            />
        </KeywordsBreakdownTableWrapper>
    );
};
